import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import {
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableRow,
	Typography,
	TextField,
	InputAdornment,
	IconButton as MIconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDebounce } from "../../../../shared/hooks";
import { useInstallmentDetails } from "../../hooks/useInstallmentDetails";
import { HtmlTooltip } from "../../../../infra/components/tooltipsCustom/tooltipsCuston";
import {
	globalBackgroundPayment,
	globalColorText,
	globalFormatCurrencyBRL,
	globalMaskDocument,
	globalTranslateMethodPayment,
	globalTranslatePayment,
	globalTranslateStatusInvoices,
} from "../../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { highlightText } from "../../../../shared/utils/highlightText";

interface Props {
	open: boolean;
	onClose: () => void;
	dataIni: string;
	dataFim: string;
	row: {
		clientId: string;
		name: string;
	};
	paymentMethod: "credit_card" | "billet";
}

export const GenericModalDetails = ({ open, onClose, dataIni, dataFim, row, paymentMethod }: Props) => {
	const [searchText, setSearchText] = useState("");
	const [debouncedSearchText, setDebouncedSearchText] = useState("");
	const { debounce } = useDebounce();

	const { rows, isLoading, fetchDetails } = useInstallmentDetails({
		dataIni,
		dataFim,
		paymentMethod,
	});

	useEffect(() => {
		debounce(() => setDebouncedSearchText(searchText));
	}, [searchText]);

	useEffect(() => {
		if (open && row?.clientId) {
			if (rows.length === 0) {
				fetchDetails(row.clientId);
			}
		}
	}, [open]);

	const normalize = (text: string) => text.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
	const activeRows = rows.filter((item) => item.Installment_status !== "canceled");
	const total = activeRows
		.filter((item) => item.Installment_type_amount !== "discount")
		.reduce((acc, item) => acc + Number(item.Installment_amount), 0);
	const descontos = activeRows
		.filter((item) => item.Installment_type_amount === "discount")
		.reduce((acc, item) => acc + Number(item.Installment_amount), 0);
	const valorLiquido = total - descontos;

	const filteredRows = [...rows]
		.sort((a, b) => a.name.localeCompare(b.name))
		.filter((item) => normalize(`${item.name} ${item.document}`).includes(normalize(debouncedSearchText)));

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
			<DialogTitle
				sx={{
					color: "primary.main",
					fontWeight: 400,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				{`Detalhes do Faturamento - ${row.name}`}
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				{isLoading ? (
					<Stack alignItems="center" py={4}>
						<CircularProgress />
						<Typography variant="body2" mt={2}>
							Carregando...
						</Typography>
					</Stack>
				) : (
					<Box sx={{ maxHeight: 500, overflowY: "auto", px: 2 }}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
							<TextField
								placeholder="Pesquisar por nome ou documento"
								variant="outlined"
								size="small"
								fullWidth
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
									endAdornment: searchText && (
										<InputAdornment position="end">
											<MIconButton size="small" onClick={() => setSearchText("")} aria-label="Limpar busca">
												<ClearIcon fontSize="small" />
											</MIconButton>
										</InputAdornment>
									),
								}}
								sx={{ maxWidth: 400 }}
							/>
							<Typography variant="body2" sx={{ ml: 2 }}>
								{filteredRows.length} registro(s) encontrado(s)
							</Typography>
						</Stack>

						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow sx={{ backgroundColor: "grey.100" }}>
									{["Certificado", "Nome", "Tipo", "Valor", "Vencimento", "Cobrança", "Observação", "Status"].map((header, i) => (
										<TableCell key={i} align={i === 4 ? "right" : i === 3 ? "center" : "left"}>
											<Typography variant="button">{header}</Typography>
										</TableCell>
									))}
								</TableRow>
							</TableHead>

							<TableBody>
								{filteredRows.map((item) => (
									<TableRow
										key={item.Installment_id}
										hover
										sx={item.Installment_type_amount === "discount" ? { backgroundColor: "rgba(255, 0, 0, 0.04)" } : undefined}
									>
										<TableCell>{item.certificate_number}</TableCell>
										<TableCell>
											<Typography fontSize="0.875rem">{highlightText(item.name, debouncedSearchText)}</Typography>
											<Typography fontSize="0.875rem" fontWeight="bold">
												{highlightText(globalMaskDocument(item.document), debouncedSearchText)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography fontSize="0.875rem">{globalTranslatePayment(item.Installment_type_amount)}</Typography>
											{item.Installment_type_amount === "parcel" && (
												<Typography variant="caption" color="textSecondary">
													{item.Installment_number}/{item.installments_count}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Typography fontWeight="bold">{globalFormatCurrencyBRL(item.Installment_amount)}</Typography>
										</TableCell>
										<TableCell>{formatDateFnsRemoveTimezone(item.Installment_due_date)}</TableCell>
										<TableCell>{globalTranslateMethodPayment(item.Installment_payment_method)}</TableCell>
										<TableCell>
											{item.Installment_obs ? (
												<HtmlTooltip
													disableHoverListener={false}
													title={
														<Typography color="inherit" fontSize="0.875rem" fontWeight={400}>
															{item.Installment_obs}
														</Typography>
													}
												>
													<Typography fontSize="0.875rem" noWrap>
														{item.Installment_obs.length > 15
															? item.Installment_obs.slice(0, 15).trim() + "..."
															: item.Installment_obs}
													</Typography>
												</HtmlTooltip>
											) : (
												"-"
											)}
										</TableCell>
										<TableCell>
											<Chip
												label={globalTranslateStatusInvoices(item.Installment_status)}
												size="small"
												sx={{
													fontWeight: 500,
													backgroundColor: globalBackgroundPayment(item.Installment_status),
													color: globalColorText(item.Installment_status),
												}}
											/>
										</TableCell>
									</TableRow>
								))}
							</TableBody>

							<TableFooter sx={{ padding: 2 }}>
								<TableRow>
									<TableCell colSpan={8}>
										<Stack spacing={1} sx={{ py: 1, width: "100%" }} alignItems="flex-end">
											<Stack direction="row" spacing={4} justifyContent="space-between" sx={{ minWidth: 300 }}>
												<Typography fontWeight={500}>Total da Fatura:</Typography>
												<Typography fontWeight={500}>{globalFormatCurrencyBRL(total.toString())}</Typography>
											</Stack>
											<Stack direction="row" spacing={4} justifyContent="space-between" sx={{ minWidth: 300 }}>
												<Typography fontWeight={500}>Descontos:</Typography>
												<Typography fontWeight={500}>{globalFormatCurrencyBRL(descontos.toString())}</Typography>
											</Stack>
											<Stack direction="row" spacing={4} justifyContent="space-between" sx={{ minWidth: 300 }}>
												<Typography fontWeight={600}>Saldo a pagar:</Typography>
												<Typography fontWeight={600} color="primary">
													{globalFormatCurrencyBRL(valorLiquido.toString())}
												</Typography>
											</Stack>
										</Stack>
									</TableCell>
								</TableRow>
							</TableFooter>
						</Table>
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};
